import { useCallback, useMemo } from 'react';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import type { FieldComponentProps } from '../fieldRegistry';

/** The value shape: one boolean per option id. Absent options are `false`. */
export type ChecklistValue = Record<string, boolean>;

/**
 * The two accessible-name descriptors, hoisted OUT of the render.
 *
 * These MUST be literal `msg({ id, message })` calls at module scope: `lingui
 * extract` reads the source statically, so a descriptor built inside the
 * `i18n._()` call — e.g. `i18n._(checked ? {...} : {...})` — is silently
 * skipped and neither id ever reaches the catalogues. Selecting between two
 * already-declared descriptors keeps both extractable.
 */
const CHECKED_LABEL = msg({ id: 'form.checklist.ariaLabel.checked', message: '{flag}: done' });
const UNCHECKED_LABEL = msg({
  id: 'form.checklist.ariaLabel.unchecked',
  message: '{flag}: not done',
});

/**
 * Registered `checklist` field (ST-5/S17).
 *
 * Renders a `<ul>` of real, focusable checkbox rows — keyboard-operable, `Space`
 * toggles — each with its own bound label and an optional description line. The
 * list keeps its native list semantics and is named by `aria-label`, since a <ul>
 * is not a labelable element and the form's `<label>` is only a caption for it.
 * Follows the `ClosureChecklistCard` row layout so the two checklists on the
 * settling-in detail page read as one system.
 *
 * State is carried on TWO channels, never colour alone:
 *   1. the checkbox's own `checked` state — announced by a screen reader and
 *      shown visually as its border, fill and tick, and
 *   2. an explicit accessible name stating the flag AND its state.
 *
 * A third channel (a check-vs-empty-square glyph beside the checkbox) was
 * dropped: lucide's `Square` renders at the same size as the Radix checkbox, so
 * an unchecked row showed two identical empty boxes and operators read the
 * decorative one as a second, broken control. It was `aria-hidden`, so removing
 * it changes nothing for assistive tech.
 */
export function ChecklistField<T = ChecklistValue>({
  field,
  value,
  onChange,
  onBlur,
  disabled,
  id,
  'aria-describedby': ariaDescribedBy,
}: FieldComponentProps<T>) {
  const { i18n } = useLingui();
  // Memoised so the `?? {}` fallback does not produce a new object identity on
  // every render, which would rebuild the toggle callback each time.
  const current = useMemo(() => (value ?? {}) as ChecklistValue, [value]);
  const options = field.checklistOptions ?? [];

  const toggle = useCallback(
    (optionValue: string, checked: boolean) => {
      onChange({ ...current, [optionValue]: checked } as T);
    },
    [current, onChange],
  );

  return (
    <ul
      id={id}
      // `aria-label` (NOT `role="group"`) so the checklist carries its own
      // accessible name: BaseEditableForm's `<label>` is a caption for composite
      // fields and cannot bind to a <ul> via htmlFor. A <ul> takes an accessible
      // name directly; overriding its implicit `role="list"` with `group` strips
      // the list semantics and orphans the <li> children (axe: "List item parent
      // element has a role that is not role=list").
      aria-label={field.label}
      aria-describedby={ariaDescribedBy}
      // Wrap-tolerant, no fixed min-widths: German descriptions run long.
      className="space-y-2"
    >
      {options.map((option) => {
        const checked = current[option.value] === true;
        const rowId = `${id}-${option.value}`;
        const descriptionId = option.description ? `${rowId}-description` : undefined;
        // The accessible name states the flag AND its state, so the control is
        // never announced as a bare label whose state must be inferred visually.
        // `i18n._` only accepts a `values` second argument alongside a STRING id
        // (see @lingui/core's two overloads), so the interpolation rides on the
        // descriptor itself. The descriptor is assembled into a VARIABLE rather
        // than spread inline: `lingui extract` walks every object literal passed
        // directly to `i18n._()` and hard-crashes on a spread element it cannot
        // resolve. Passing an identifier makes it skip this call site, which is
        // correct — both ids are already extracted from the module-level `msg()`
        // constants above.
        const labelDescriptor = {
          ...(checked ? CHECKED_LABEL : UNCHECKED_LABEL),
          values: { flag: option.label },
        };
        const accessibleName = i18n._(labelDescriptor);

        return (
          <li key={option.value} className="flex items-start gap-2 text-sm">
            <Checkbox
              id={rowId}
              // `name` is load-bearing: ClosureChecklistCard's jump-to-fix does a
              // `document.querySelector('[name="info_confirmed"]')` focus, and
              // that selector must still resolve after S17.
              //
              // Radix consumes the `name` PROP for its hidden form-bubble input
              // (which is not rendered outside a <form>, and is not focusable
              // anyway), so it never reaches the visible button. Setting the
              // attribute directly on the focusable button is what actually keeps
              // that selector resolving — passing `name={...}` alone silently
              // breaks the jump.
              ref={(el: HTMLButtonElement | null) => {
                el?.setAttribute('name', option.value);
              }}
              checked={checked}
              disabled={disabled}
              onCheckedChange={(next) => toggle(option.value, next === true)}
              onBlur={onBlur}
              aria-label={accessibleName}
              aria-describedby={descriptionId}
              className="mt-0.5 shrink-0"
            />
            <div className="min-w-0 flex-1">
              <label
                htmlFor={rowId}
                className={cn(
                  'block cursor-pointer',
                  checked ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {option.label}
              </label>
              {option.description && (
                <span id={descriptionId} className="block text-xs text-muted-foreground">
                  {option.description}
                </span>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
